// Row shapes of the RLS-protected read models and RPCs (migrations 20260912140000_web_read_models and the
// workflow tables). Shared so web and mobile read the same columns.

/** Company roles (CR_Manager_vs_User_role P8). `user` is the legacy value, gone after that CR's migration. */
export type Role = 'manager' | 'warehouse_staff' | 'project_user' | 'user';
export type Scope = 'none' | 'selected' | 'all';

export interface Whoami {
  user_id: string;
  phone: string | null;
  email: string | null;
  full_name: string | null;
  locale: 'en' | 'ms' | 'zh';
  platform_admin: boolean;
  company: { id: string; name: string; code: string; timezone: string } | null;
  role: Role | null;
  /** mirror for old builds: `all` for managers and warehouse staff with all warehouses + all projects */
  site_access: 'all' | 'selected' | null;
  /** the sites the person may see and work at (role + scopes + grants, computed by the server) */
  site_ids: string[];
  warehouse_scope?: Scope | null;
  project_scope?: Scope | null;
  project_ids?: string[];
  /** Set while a super admin works inside `company` (support mode, CR_20260913; as a chosen role, CR_20260924). */
  support?: SupportInfo | null;
  /** The role or active flag changed after this session signed in: sign in again (2026-09-25). */
  reauth?: boolean;
}

/** A place by name for transfers (CR_Manager_vs_User_role P18): what the caller may see or send to — never stock. */
export interface TransferPlace {
  site_id: string;
  name: string;
  type: SiteOverview['type'];
  project_id: string | null;
  project_name: string | null;
  is_active: boolean;
  /** a place the caller works at (may send from, receive at) */
  is_mine: boolean;
}

export interface SupportInfo {
  session_id: string;
  company_id: string;
  company_name: string;
  company_code: string;
  reason: string;
  started_at: string;
  expires_at: string;
  /** The role the visit acts as, and its reach (CR_20260924 Part C). Absent from servers before that migration. */
  act_as?: SupportRole;
  act_scope?: 'all' | 'selected';
  act_site_ids?: string[];
  act_project_ids?: string[];
}

/** The roles a support visit can act as (CR_20260924 Part C) — never the legacy `user`. */
export type SupportRole = 'manager' | 'warehouse_staff' | 'project_user';

export interface SiteOverview {
  site_id: string;
  company_id: string;
  name: string;
  type: 'yard' | 'warehouse' | 'project_site' | 'customer_site';
  project_id: string | null;
  is_active: boolean;
  /** the custody ledger: active assets this place is accountable for */
  expected: number;
  /** of `expected`, standing here */
  on_site: number;
  seen_last_hour: number;
  /**
   * Physically here on an open transfer in, still in the sender's custody — inbound work, NOT part of `expected`.
   * Receiving them moves custody here and they join `expected` and `on_site`.
   */
  awaiting_receipt: number;
  /** of `expected`, reported missing */
  missing: number;
  gateways: number;
  gateways_online: number;
  /** active zones (CR_20260914) */
  zones: number;
  /** of `expected`, on a lorry */
  in_transit: number;
  /** of `expected`, sighted somewhere else (incl. a sibling location of the same project) */
  off_site: number;
  /** of `expected`, never reported: no tag, no gateway, nobody scanned it */
  not_seen: number;
  /** everything standing here whoever owns it (`on_site` + arrivals + strays) — the map label */
  present_total: number;
}

/** A named area inside a site (CR_20260914). Archived zones keep their name for history. */
export interface SiteZone {
  id: string;
  company_id: string;
  site_id: string;
  name: string;
  note: string | null;
  is_active: boolean;
}

export type ZoneMoveReason =
  'moved' | 'received' | 'custody_changed' | 'zone_archived' | 'asset_retired';

/** One asset_zone_moves row with the names a list needs. */
export interface ZoneMove {
  id: number;
  asset_id: string;
  site_id: string;
  reason: ZoneMoveReason;
  at: string;
  assets: { asset_code: string } | null;
  from_zone: { name: string } | null;
  to_zone: { name: string } | null;
}

export type DisplayState =
  'on_site' | 'in_transit' | 'off_site' | 'missing' | 'unknown' | 'untagged' | 'awaiting_receipt';

export interface AssetOverview {
  asset_id: string;
  company_id: string;
  asset_code: string;
  description: string | null;
  make_model: string | null;
  serial: string | null;
  asset_status: string;
  category_id: string | null;
  category_name: string | null;
  custody_site_id: string | null;
  custody_site_name: string | null;
  presence_state: string | null;
  presence_site_id: string | null;
  presence_site_name: string | null;
  since_at: string | null;
  last_seen_at: string | null;
  last_source: string | null;
  vehicle_ref: string | null;
  /** last GPS / lorry fix */
  lat: number | null;
  lng: number | null;
  has_electronic_tag: boolean;
  open_transfer_id: string | null;
  open_transfer_ref: string | null;
  open_transfer_status: string | null;
  open_transfer_to_site_id: string | null;
  display_state: DisplayState;
  /** where in the custody site it was last put (CR_20260914); null name = zone not visible to this user */
  zone_id: string | null;
  zone_name: string | null;
  zone_set_at: string | null;
  zone_set_by_name: string | null;
  // Removed from service (CR_20260920_Asset_Register). Optional: fixtures and builds against an older view lack them.
  retire_stage?: RetireStage;
  /** the date it left service, company timezone (YYYY-MM-DD) */
  retired_at?: string | null;
  retired_by_name?: string | null;
  retire_note?: string | null;
  /** null on an asset retired before the register existed */
  disposal_reason?: DisposalReason | null;
  /** numeric from PostgREST: a string */
  disposal_proceeds?: string | number | null;
  retire_confirmed_at?: string | null;
  retire_confirmed_by_name?: string | null;
  /** the viewer may confirm the tag is off (warehouse staff at its site; managers only when none can) */
  can_confirm_tag_removal?: boolean;
  /** awaiting tag removal: true = warehouse staff do it, false = nobody on staff reaches the site (managers do) */
  tag_removal_by_staff?: boolean | null;
  /** when step 1 was recorded (retired_at is the date given, which may be earlier) */
  retire_recorded_at?: string | null;
  /** who registered it and when (the Assets list's Registered column, CR_20260920_Asset_Register §9) */
  created_at?: string;
  created_by_name?: string | null;
  /**
   * Where it is by the newest evidence (2026-09-27): the engine's state for a tagged asset; for a QR-only asset its
   * last sighting or, when newer (and after its last receipt), its last stock-count confirmation = on site there.
   */
  seen_state?: Exclude<DisplayState, 'untagged' | 'awaiting_receipt'> | null;
  seen_site_id?: string | null;
  seen_site_name?: string | null;
  /** last confirmation in a stock count (also folded into last_seen_at) */
  last_counted_at?: string | null;
  /** what it carries besides its QR label */
  label_kind?: 'qr' | 'ble' | 'gps_tracker';
  /** worst state of the certificates its category requires; null = none required (CR_20260921 T1) */
  cert_state?: 'valid' | 'expiring' | 'expired' | 'missing' | null;
  /** the latest condition report (CR_20260921 T2) */
  condition?: 'good' | 'fair' | 'poor' | 'unserviceable' | null;
  condition_at?: string | null;
  /** out of service blocks dispatch and pauses billing; the asset is still here and still counted (L15) */
  service_state?: 'in_service' | 'out_of_service';
  service_reason?: string | null;
}

/** active · awaiting_tag_removal (step 1 done, a device is still on it) · retired (complete) */
export type RetireStage = 'active' | 'awaiting_tag_removal' | 'retired';

export const DISPOSAL_REASONS = [
  'scrapped',
  'sold',
  'lost',
  'damaged_beyond_repair',
  'returned_to_owner',
] as const;
export type DisposalReason = (typeof DISPOSAL_REASONS)[number];

export type HistoryEventKind =
  | 'entered'
  | 'exited'
  | 'transit_start'
  | 'transit_end'
  | 'off_site'
  | 'missing'
  | 'found'
  | 'manual';

export interface HistoryEvent {
  id: number;
  event: HistoryEventKind;
  site_id: string | null;
  vehicle_ref: string | null;
  at: string;
  source: string | null;
  sites: { name: string } | null;
}

export type TransferStatus =
  'draft' | 'dispatched' | 'awaiting_receipt' | 'received' | 'disputed' | 'cancelled';

export type LineStatus = 'pending' | 'received' | 'not_received' | 'disputed' | 'superseded';

export interface TransferLine {
  id: string;
  asset_id: string;
  status: LineStatus;
  assets: { asset_code: string; description: string | null } | null;
}

export interface Transfer {
  id: string;
  ref: string;
  status: TransferStatus;
  from_site_id: string | null;
  to_site_id: string | null;
  created_by_system: boolean;
  vehicle_ref: string | null;
  dispatched_at: string | null;
  expected_at: string | null;
  awaiting_since: string | null;
  created_at: string;
  from_site: { name: string } | null;
  to_site: { name: string } | null;
  transfer_lines: TransferLine[];
  /** a manager dispatched it past a certificate block (CR_20260921 L6a); absent when not selected */
  cert_override?: CertOverride | null;
}

export interface CertOverride {
  by: string | null;
  by_name: string | null;
  at: string;
  reason: string;
  assets: {
    asset_id: string;
    code: string;
    kind: string;
    reason: string;
    expires_on: string | null;
  }[];
}

/** A tag as the Nearby / install screens see it (tags + attached asset). */
export interface TagRow {
  id: string;
  type: 'ble_beacon' | 'ble_sensor' | 'gps_tracker' | 'qr' | 'nfc';
  identifier: string;
  status: 'in_stock' | 'allocated' | 'faulty' | 'retired';
  asset_id: string | null;
  battery_pct: number | null;
  last_seen_at: string | null;
  assets: { asset_code: string; description: string | null } | null;
}
